/**
 * CR225 Addendum: Preview Fixtures
 * 
 * Deterministic test fixtures for UI control testing.
 * These flow through the EXACT same pipeline as real data:
 * Fixture → toUiDeal() → Builder config → renderer
 * 
 * Client-side only - no DB writes.
 */

import type { RawDealInput } from "./toUiDeal";

// ================================================================
// FIXTURE IDS
// ================================================================

export type PreviewFixtureId = 
  | "real_data"
  | "has_starburst"
  | "has_ribbon_starburst"
  | "expired"
  | "upcoming"
  | "missing_dates"
  | "evergreen"       // CR292: Standing offer with dates_type=EVERGREEN
  | "full_payload"
  | "complete_mock"  // CR290C: Shows all blocks/sections reliably
  | "all_deal_types"; // CR290C: One of each deal type

export interface PreviewFixtureConfig {
  id: PreviewFixtureId;
  label: string;
  description: string;
  icon: "database" | "star" | "ribbon" | "clock" | "calendar" | "alert-triangle" | "sparkles";
}

export const PREVIEW_FIXTURES: PreviewFixtureConfig[] = [
  {
    id: "real_data",
    label: "Real Data",
    description: "Use currently selected data source",
    icon: "database",
  },
  {
    id: "has_starburst",
    label: "Has Starburst",
    description: "Deal with complete value payload for starburst badge",
    icon: "star",
  },
  {
    id: "has_ribbon_starburst",
    label: "Ribbon + Starburst",
    description: "Deal with ribbon text AND starburst value",
    icon: "ribbon",
  },
  {
    id: "expired",
    label: "Expired",
    description: "Deal that has already ended",
    icon: "clock",
  },
  {
    id: "upcoming",
    label: "Upcoming",
    description: "Deal that hasn't started yet",
    icon: "calendar",
  },
  {
    id: "missing_dates",
    label: "Missing Dates",
    description: "Deal with unclear/missing date information",
    icon: "alert-triangle",
  },
  {
    id: "evergreen",
    label: "Evergreen (No Dates)",
    description: "CR292: Standing offer with date_mode=ONGOING or dates_type=EVERGREEN - no warning",
    icon: "star",
  },
  {
    id: "full_payload",
    label: "Full Payload",
    description: "Deal with ALL optional fields populated",
    icon: "sparkles",
  },
  {
    id: "complete_mock",
    label: "Complete Mock",
    description: "CR290C: Populates ALL blocks and sections reliably",
    icon: "sparkles",
  },
  {
    id: "all_deal_types",
    label: "All Deal Types",
    description: "CR290C: One of each deal type (BONUS, DISCOUNT, POINTS, IN_KIND)",
    icon: "database",
  },
];

// ================================================================
// FIXTURE DATA
// ================================================================

const now = new Date();
const dayInMs = 24 * 60 * 60 * 1000;

/**
 * Get fixture deals for a given fixture ID
 * Returns array of RawDealInput that flow through toUiDeal()
 */
export function getFixtureDeals(fixtureId: PreviewFixtureId): RawDealInput[] {
  switch (fixtureId) {
    case "real_data":
      return []; // Empty = use real data source
      
    case "has_starburst":
      return [createStarburstDeal()];
      
    case "has_ribbon_starburst":
      return [createRibbonStarburstDeal()];
      
    case "expired":
      return [createExpiredDeal()];
      
    case "upcoming":
      return [createUpcomingDeal()];
      
    case "missing_dates":
      return [createMissingDatesDeal()];
    
    case "evergreen":
      return [createEvergreenDeal()];
      
    case "full_payload":
      return [createFullPayloadDeal()];
    
    case "complete_mock":
      return createCompleteMockDeals();
    
    case "all_deal_types":
      return createAllDealTypesFixture();
      
    default:
      return [];
  }
}

// ================================================================
// FIXTURE FACTORIES
// ================================================================

function createStarburstDeal(): RawDealInput {
  return {
    id: "fixture-starburst-001",
    merchant_name: "Shoppers Drug Mart",
    promo_merchant_name: "Shoppers Drug Mart",
    reward_brand_name: "The Keg",
    headline_mechanic: "Buy $100 in The Keg gift cards, get $25 bonus card",
    deal_category: "BONUS",
    deal_type: "bonus",
    // STARBURST PAYLOAD: These fields enable starburst rendering
    bonus_value_cad: 25,
    qualifying_spend_cad: 100,
    effective_percent_back: 25, // This is what starburst shows
    computed_value_cad: 25,
    // Dates
    valid_from: new Date(now.getTime() - 2 * dayInMs).toISOString(),
    valid_until: new Date(now.getTime() + 7 * dayInMs).toISOString(),
    // Source
    source_type: "flyer",
    source_url: "https://example.com/shoppers-flyer",
    purchase_gift_card_brand: "The Keg",
    gift_card_brand: "The Keg",
    fine_print: "Limit 2 per customer. Bonus card valid 30 days after issue.",
    confidence: "High",
  };
}

function createRibbonStarburstDeal(): RawDealInput {
  return {
    id: "fixture-ribbon-starburst-001",
    merchant_name: "Metro",
    promo_merchant_name: "Metro",
    reward_brand_name: "Boston Pizza",
    headline_mechanic: "LIMITED TIME: Buy $50, Get $15 Bonus!",
    deal_category: "BONUS",
    deal_type: "bonus",
    // STARBURST PAYLOAD
    bonus_value_cad: 15,
    qualifying_spend_cad: 50,
    effective_percent_back: 30,
    computed_value_cad: 15,
    // Dates
    valid_from: new Date(now.getTime() - 1 * dayInMs).toISOString(),
    valid_until: new Date(now.getTime() + 3 * dayInMs).toISOString(),
    // Source
    source_type: "flyer",
    purchase_gift_card_brand: "Boston Pizza",
    gift_card_brand: "Boston Pizza",
    fine_print: "While supplies last. One per customer.",
    confidence: "High",
    // Note: Ribbon text comes from deal type registry, not deal payload
    // But this deal has short end_date which often triggers "Ends Soon" ribbon
  };
}

function createExpiredDeal(): RawDealInput {
  return {
    id: "fixture-expired-001",
    merchant_name: "Walmart",
    promo_merchant_name: "Walmart",
    reward_brand_name: "Walmart",
    headline_mechanic: "Was 10% off all gift cards",
    deal_category: "DISCOUNT",
    deal_type: "discount",
    discount_percent: 10,
    effective_percent_back: 10,
    // EXPIRED: end_date in the past
    valid_from: new Date(now.getTime() - 14 * dayInMs).toISOString(),
    valid_until: new Date(now.getTime() - 1 * dayInMs).toISOString(),
    source_type: "website",
    purchase_gift_card_brand: "Walmart",
    confidence: "High",
  };
}

function createUpcomingDeal(): RawDealInput {
  return {
    id: "fixture-upcoming-001",
    merchant_name: "Costco",
    promo_merchant_name: "Costco",
    reward_brand_name: "Apple",
    headline_mechanic: "Coming Soon: $20 off $100 Apple Gift Cards",
    deal_category: "DISCOUNT",
    deal_type: "discount",
    discount_percent: 20,
    effective_percent_back: 20,
    computed_value_cad: 20,
    // UPCOMING: start_date in the future
    valid_from: new Date(now.getTime() + 3 * dayInMs).toISOString(),
    valid_until: new Date(now.getTime() + 10 * dayInMs).toISOString(),
    source_type: "website",
    purchase_gift_card_brand: "Apple",
    confidence: "High",
  };
}

function createMissingDatesDeal(): RawDealInput {
  return {
    id: "fixture-missing-dates-001",
    merchant_name: "Sobeys",
    promo_merchant_name: "Sobeys",
    reward_brand_name: "Swiss Chalet",
    headline_mechanic: "Buy $50, get $10 bonus",
    deal_category: "BONUS",
    deal_type: "bonus",
    bonus_value_cad: 10,
    qualifying_spend_cad: 50,
    effective_percent_back: 20,
    // MISSING DATES: No valid_from/valid_until
    valid_from: null,
    valid_until: null,
    source_type: "website",
    purchase_gift_card_brand: "Swiss Chalet",
    fine_print: "Valid dates unknown. Check store for details.",
    confidence: "Low",
  };
}

/**
 * CR292: Evergreen deal fixture - standing offer with no time window
 * This should NOT show a "missing dates" warning
 */
function createEvergreenDeal(): RawDealInput {
  return {
    id: "fixture-evergreen-001",
    merchant_name: "The Keg",
    promo_merchant_name: "The Keg",
    reward_brand_name: "The Keg",
    headline_mechanic: "Save 10% on all gift card purchases",
    deal_category: "DISCOUNT",
    deal_type: "discount",
    discount_percent: 10,
    qualifying_spend_cad: 100,
    effective_percent_back: 10,
    computed_value_cad: 10,
    // CR292: EVERGREEN - no dates, but dates_type/date_mode indicates this is intentional
    valid_from: null,
    valid_until: null,
    dates_type: "EVERGREEN",
    canonical_deal_json: {
      dates: {
        date_mode: "ONGOING", // Also set date_mode for testing
      }
    },
    date_phrase: null, // Standing offer - no date phrase needed
    source_type: "website",
    source_url: "https://www.thekeg.com/gift-cards",
    purchase_gift_card_brand: "The Keg",
    fine_print: "Available year-round. No expiry on gift cards.",
    confidence: "High",
  };
}

function createFullPayloadDeal(): RawDealInput {
  return {
    id: "fixture-full-payload-001",
    merchant_name: "Loblaws",
    promo_merchant_name: "Loblaws",
    reward_brand_name: "Uber Eats",
    headline_mechanic: "FEATURED: Buy $100 Uber Eats, Get $20 Bonus + 2000 PC Points!",
    deal_category: "BONUS",
    deal_type: "bonus",
    // All value fields
    bonus_value_cad: 20,
    qualifying_spend_cad: 100,
    effective_percent_back: 20,
    computed_value_cad: 20,
    points_amount: 2000,
    points_program_name: "PC Optimum",
    // Full date range
    valid_from: new Date(now.getTime() - 1 * dayInMs).toISOString(),
    valid_until: new Date(now.getTime() + 14 * dayInMs).toISOString(),
    // Full source
    source_type: "flyer",
    source_url: "https://example.com/loblaws-flyer",
    flyer_page_number: 3,
    flyer_deeplink_url: "https://example.com/loblaws-flyer#page3",
    // All branding
    purchase_gift_card_brand: "Uber Eats",
    gift_card_brand: "Uber Eats",
    bonus_merchant: "Loblaws",
    // All meta
    fine_print: "Limit one per household. Must be a PC Optimum member. Bonus card valid 60 days after purchase.",
    exclusions: "Cannot be combined with other offers. Not valid on previous purchases.",
    confidence: "High",
    region: "ON",
    // Eligibility
    availability_scope: "in_store",
    availability_notes: "Available at all Ontario Loblaws locations",
    eligibility_scope: "consumer",
    audience_hint: "general",
  };
}

// ================================================================
// CR290C: COMPLETE MOCK FIXTURES
// ================================================================

/**
 * CR290C: Create a set of deals that populate ALL UI blocks reliably.
 * Use this in Layout Studio to verify all sections render correctly.
 */
function createCompleteMockDeals(): RawDealInput[] {
  return [
    // BONUS deal with complete payload
    {
      id: "complete-mock-bonus",
      merchant_name: "Shoppers Drug Mart",
      promo_merchant_name: "Shoppers Drug Mart",
      reward_brand_name: "Air Canada",
      headline_mechanic: "Buy $250 Air Canada, get $50 bonus Shoppers card",
      deal_category: "BONUS",
      deal_type: "bonus",
      bonus_value_cad: 50,
      qualifying_spend_cad: 250,
      effective_percent_back: 20,
      computed_value_cad: 50,
      valid_from: new Date(now.getTime() - 1 * dayInMs).toISOString(),
      valid_until: new Date(now.getTime() + 10 * dayInMs).toISOString(),
      source_type: "flyer",
      source_url: "https://example.com/shoppers-flyer",
      purchase_gift_card_brand: "Air Canada",
      gift_card_brand: "Air Canada",
      fine_print: "Limit 2 per customer. Bonus card valid 30 days.",
      exclusions: "Not combinable with other offers.",
      claim_url: "https://shoppers.ca/promo",
      confidence: "High",
      region: "ON",
      flyer_page_number: 2,
    },
    // DISCOUNT deal
    {
      id: "complete-mock-discount",
      merchant_name: "Costco",
      promo_merchant_name: "Costco",
      reward_brand_name: "Google Play",
      headline_mechanic: "15% off Google Play gift cards",
      deal_category: "DISCOUNT",
      deal_type: "discount",
      discount_percent: 15,
      effective_percent_back: 15,
      computed_value_cad: 15,
      valid_from: new Date(now.getTime() - 2 * dayInMs).toISOString(),
      valid_until: new Date(now.getTime() + 5 * dayInMs).toISOString(),
      source_type: "website",
      source_url: "https://costco.ca/deals",
      purchase_gift_card_brand: "Google Play",
      fine_print: "While supplies last.",
      confidence: "High",
    },
    // POINTS deal
    {
      id: "complete-mock-points",
      merchant_name: "Loblaws",
      promo_merchant_name: "Loblaws",
      reward_brand_name: "iTunes",
      headline_mechanic: "Get 5000 PC Points on $50 iTunes",
      deal_category: "POINTS",
      deal_type: "points",
      points_amount: 5000,
      points_program_name: "PC Optimum",
      qualifying_spend_cad: 50,
      effective_percent_back: 10,
      computed_value_cad: 5,
      valid_from: new Date(now.getTime()).toISOString(),
      valid_until: new Date(now.getTime() + 7 * dayInMs).toISOString(),
      source_type: "flyer",
      purchase_gift_card_brand: "iTunes",
      confidence: "High",
    },
    // IN_KIND deal
    {
      id: "complete-mock-in-kind",
      merchant_name: "Tim Hortons",
      promo_merchant_name: "Tim Hortons",
      reward_brand_name: "Tim Hortons",
      headline_mechanic: "Free coffee with $20 gift card purchase",
      deal_category: "IN_KIND",
      deal_type: "in_kind",
      qualifying_spend_cad: 20,
      computed_value_cad: 2,
      effective_percent_back: 10,
      valid_from: new Date(now.getTime() - 1 * dayInMs).toISOString(),
      valid_until: new Date(now.getTime() + 14 * dayInMs).toISOString(),
      source_type: "website",
      purchase_gift_card_brand: "Tim Hortons",
      fine_print: "One free medium coffee per purchase.",
      confidence: "Medium",
    },
  ];
}

/**
 * CR290C: Create one deal of each type for comprehensive testing.
 */
function createAllDealTypesFixture(): RawDealInput[] {
  return [
    // BONUS
    {
      id: "fixture-type-bonus",
      merchant_name: "Metro",
      promo_merchant_name: "Metro",
      reward_brand_name: "Boston Pizza",
      headline_mechanic: "Buy $50, get $10 bonus",
      deal_category: "BONUS",
      deal_type: "bonus",
      bonus_value_cad: 10,
      qualifying_spend_cad: 50,
      effective_percent_back: 20,
      computed_value_cad: 10,
      valid_from: new Date(now.getTime() - 1 * dayInMs).toISOString(),
      valid_until: new Date(now.getTime() + 7 * dayInMs).toISOString(),
      source_type: "flyer",
      purchase_gift_card_brand: "Boston Pizza",
      confidence: "High",
    },
    // DISCOUNT
    {
      id: "fixture-type-discount",
      merchant_name: "Walmart",
      promo_merchant_name: "Walmart",
      reward_brand_name: "Netflix",
      headline_mechanic: "10% off Netflix gift cards",
      deal_category: "DISCOUNT",
      deal_type: "discount",
      discount_percent: 10,
      effective_percent_back: 10,
      valid_from: new Date(now.getTime()).toISOString(),
      valid_until: new Date(now.getTime() + 5 * dayInMs).toISOString(),
      source_type: "website",
      purchase_gift_card_brand: "Netflix",
      confidence: "High",
    },
    // POINTS
    {
      id: "fixture-type-points",
      merchant_name: "Sobeys",
      promo_merchant_name: "Sobeys",
      reward_brand_name: "Starbucks",
      headline_mechanic: "Earn 1000 Scene+ points on $25 Starbucks",
      deal_category: "POINTS",
      deal_type: "points",
      points_amount: 1000,
      points_program_name: "Scene+",
      qualifying_spend_cad: 25,
      effective_percent_back: 4,
      valid_from: new Date(now.getTime()).toISOString(),
      valid_until: new Date(now.getTime() + 10 * dayInMs).toISOString(),
      source_type: "flyer",
      purchase_gift_card_brand: "Starbucks",
      confidence: "High",
    },
    // IN_KIND
    {
      id: "fixture-type-in-kind",
      merchant_name: "Best Buy",
      promo_merchant_name: "Best Buy",
      reward_brand_name: "Xbox",
      headline_mechanic: "Free game with $100 Xbox gift card",
      deal_category: "IN_KIND",
      deal_type: "in_kind",
      qualifying_spend_cad: 100,
      computed_value_cad: 30,
      effective_percent_back: 30,
      valid_from: new Date(now.getTime() - 2 * dayInMs).toISOString(),
      valid_until: new Date(now.getTime() + 12 * dayInMs).toISOString(),
      source_type: "website",
      purchase_gift_card_brand: "Xbox",
      fine_print: "While supplies last. Selected games only.",
      confidence: "Medium",
    },
  ];
}

// ================================================================
// PAYLOAD DETECTION HELPERS
// ================================================================

export interface PayloadPresenceResult {
  hasRibbonPayload: boolean;
  hasStarburstPayload: boolean;
  hasDatesPayload: boolean;
  hasValuePayload: boolean;
  ribbonReason: string;
  starburstReason: string;
}

/**
 * Check what payload is present on a deal for UI indicators
 */
export function checkPayloadPresence(deal: RawDealInput | null | undefined): PayloadPresenceResult {
  if (!deal) {
    return {
      hasRibbonPayload: false,
      hasStarburstPayload: false,
      hasDatesPayload: false,
      hasValuePayload: false,
      ribbonReason: "No deal provided",
      starburstReason: "No deal provided",
    };
  }
  
  // Starburst requires value payload
  const hasPercentBack = typeof deal.effective_percent_back === "number" && deal.effective_percent_back > 0;
  const hasBonusValue = typeof deal.bonus_value_cad === "number" && deal.bonus_value_cad > 0;
  const hasDiscountPercent = typeof deal.discount_percent === "number" && deal.discount_percent > 0;
  const hasPointsAmount = typeof deal.points_amount === "number" && deal.points_amount > 0;
  
  const hasStarburstPayload = hasPercentBack || hasBonusValue || hasDiscountPercent || hasPointsAmount;
  
  // Ribbon - typically driven by registry, not payload
  // But some deals have implicit ribbon states (e.g., "Ends Soon" based on dates)
  const hasRibbonPayload = true; // Ribbon visibility is usually controlled by registry, not payload
  
  // Dates
  const hasDatesPayload = !!(
    deal.valid_from || deal.valid_until ||
    deal.start_date || deal.end_date
  );
  
  // Value
  const hasValuePayload = hasPercentBack || hasBonusValue || hasDiscountPercent || hasPointsAmount;
  
  return {
    hasRibbonPayload,
    hasStarburstPayload,
    hasDatesPayload,
    hasValuePayload,
    ribbonReason: "Ribbon visibility controlled by Deal Type Registry",
    starburstReason: hasStarburstPayload 
      ? `Has: ${[
          hasPercentBack && "effective_percent_back",
          hasBonusValue && "bonus_value_cad", 
          hasDiscountPercent && "discount_percent",
          hasPointsAmount && "points_amount",
        ].filter(Boolean).join(", ")}`
      : "Missing: needs effective_percent_back, bonus_value_cad, discount_percent, or points_amount",
  };
}
